import { combineReducers } from 'redux'
import { actions as notyActions } from 'layouts/ErrorBox'
import { authActions, fetchUserImage } from 'store/user'
import { modalActions } from 'store/modal'
import request from 'utils/request'
import { LOCAL_STORAGE_KEYS } from 'utils/constants'
import { getPasswordError, getEmailError } from 'utils/misc'
import { loadingActions } from 'store/loader'
import { navigateTo } from 'navigation'

const FETCHED_USER = 'FETCHED_USER'
const UPDATE_FIELD = 'UPDATE_FIELD'
const SHOW_CHANGE_PASSWORD = 'SHOW_CHANGE_PASSWORD'
const UPDATE_NEW_PASSWORD = 'UPDATE_NEW_PASSWORD'
const RESET_PW_STATE = 'RESET_PW_STATE'
const UPLOADED_IMAGE_PREVIEW = 'UPLOADED_IMAGE_PREVIEW'
const SAVING_USER = 'SAVING_USER'
const SAVED_USER = 'SAVED_USER'
const CLEAR_USER_PIC = 'CLEAR_USER_PIC'
const RESET_USER_PAGE = 'RESET_USER_PAGE'

//
// Action Creators
//

function updateField(field, value) {
  return {
    type: UPDATE_FIELD,
    field,
    value
  }
}

function updateNewPassword(field, value) {
  return {
    type: UPDATE_NEW_PASSWORD,
    field,
    value
  }
}

function showChangePassword() {
  return {
    type: SHOW_CHANGE_PASSWORD
  }
}

function resetUserPage() {
  return {
    type: RESET_USER_PAGE
  }
}

//
// API Util Functions
//

// Fetch individual user by ID
function fetchUser(id) {
  return dispatch => {
    dispatch(loadingActions.startLoader(true))
    let user
    dispatch({
      type: CLEAR_USER_PIC
    })
    const success = jsonBody => {
      user = jsonBody
      return dispatch(fetchProfilePic(user.id))
    }
    return dispatch(
      request({
        url: `/control/users/${id}`,
        success
      })
    )
      .then(() => {
        dispatch({
          type: FETCHED_USER,
          user
        })
        dispatch(loadingActions.stopLoader(true))
      })
      .catch(err => {
        dispatch(notyActions.showError({ text: err.message }))
      })
  }
}

export function fetchProfilePic(id) {
  return dispatch => {
    const success = jsonBody => {
      dispatch({
        type: UPLOADED_IMAGE_PREVIEW,
        image: jsonBody.asset
      })
    }
    return dispatch(
      request({
        url: `/control/assets/profile_pics/${id}`,
        success
      })
    )
  }
}

// Save user to database
function saveToDatabase(user, type) {
  return dispatch => {
    const userParams = Object.assign({}, user)
    if (!validateUser(userParams, dispatch)) return
    const successMessage =
      type === 'create'
        ? 'User successfully created!'
        : 'User successfully edited!'
    const url =
      type === 'create' ? '/control/users' : `/control/users/${user.id}`
    const method = type === 'create' ? 'POST' : 'PATCH'
    const success = () => {
      navigateTo('/users')
    }
    return dispatch(
      request({
        method,
        url,
        success,
        successMessage,
        body: JSON.stringify(userParams)
      })
    )
  }
}

function validateUser(params, dispatch) {
  let errorMessage = ''
  const emailError = getEmailError(params.username)
  if (emailError) errorMessage += `${emailError}<br>`
  if (!params.nickname) errorMessage += 'Nickname cannot be empty<br>'
  if (errorMessage !== '') {
    dispatch(notyActions.showError({ text: errorMessage }))
  }
  return errorMessage === ''
}

function saveSelfToDatabase(
  self,
  newPasswordActive,
  changePasswordState,
  profilePic = null
) {
  return dispatch => {
    const updatedSelf = Object.assign({}, self)
    if (newPasswordActive) {
      if (!changePasswordState.oldPassword) {
        return dispatch(
          notyActions.showError({ text: 'Please enter old password.' })
        )
      }
      const passwordError = getPasswordError(
        changePasswordState.newPassword1,
        changePasswordState.newPassword2,
        dispatch
      )
      if (passwordError) return
      updatedSelf.old_password = changePasswordState.oldPassword
      updatedSelf.new_password = changePasswordState.newPassword1
    }
    dispatch({
      type: SAVING_USER
    })
    const success = () => {
      if (profilePic) dispatch(saveProfilePicToAssets(self.id, profilePic))
      const user = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.user))
      user.nickname = self.nickname
      localStorage.setItem(LOCAL_STORAGE_KEYS.user, JSON.stringify(user))
      dispatch(authActions.updateUsername(self.nickname))
      dispatch({ type: SAVED_USER })
    }
    const fail = (res, content) => {
      dispatch({ type: SAVED_USER })
      dispatch(notyActions.showError({ text: content.message, timeout: 2000 }))
    }
    return dispatch(
      request({
        method: 'PATCH',
        url: '/control/users/me',
        successMessage: 'User info updated.',
        success,
        fail,
        body: JSON.stringify(updatedSelf)
      })
    )
  }
}

function saveProfilePicToAssets(userId, profilePic) {
  return dispatch => {
    const success = () => {
      return dispatch(fetchUserImage(userId))
    }
    return dispatch(
      request({
        method: 'PUT',
        url: `/control/assets/profile_pics/${userId}`,
        body: profilePic,
        success
      })
    )
  }
}

function deleteUser(id) {
  return dispatch => {
    const onConfirm = () => {
      dispatch(_deleteUser(id))
    }
    return dispatch(
      modalActions.openModal({
        content:
          'Are you sure you want to delete this user? This cannot be undone.',
        confirmButton: 'Yes',
        cancelButton: 'No',
        onConfirm
      })
    )
  }
}

// Delete set of flagged user IDs
function _deleteUser(id) {
  return dispatch => {
    return dispatch(
      request({
        method: 'DELETE',
        url: `/control/users/${id}`,
        failMessage:
          'There was a problem deleting this user. Please try again.',
        successMessage: 'User successfully deleted.',
        success: () => {
          navigateTo('/users')
        }
      })
    )
  }
}

function uploadProfilePic(imageFile) {
  return dispatch => {
    try {
      readImageFile(imageFile, function (imageUrl) {
        dispatch({
          type: UPLOADED_IMAGE_PREVIEW,
          image: imageUrl
        })
      })
    } catch (err) {
      dispatch(notyActions.showError({ text: err.message }))
    }
  }
}

function readImageFile(file, callback) {
  const reader = new FileReader()
  reader.onloadend = () => {
    callback(reader.result)
  }
  reader.readAsDataURL(file)
}

const ROLES = {
  enroller: ['rp', 'wp', 'xp', 'wu'],
  researcher: ['rd', 'ra', 'wa', 'da'],
  studyManager: ['rd', 'ra', 'wa', 'da', 'rp', 'wp', 'xp'],
  administrator: ['ra', 'wa', 'da', 'ru', 'wu', 'xu', 'rp', 'wp', 'xp', 'rd']
}

//
// Util Functions
//

function generateBlankUser() {
  return {
    username: '',
    created_by: null,
    scope: ROLES.enroller,
    role: 'enroller',
    nickname: ''
  }
}

function generateBlankPWState() {
  return {
    oldPassword: '',
    newPassword1: '',
    newPassword2: ''
  }
}

//
// Reducers
//

export function user(state = generateBlankUser(), action) {
  switch (action.type) {
    case FETCHED_USER: {
      return action.user
    }
    case UPDATE_FIELD: {
      const newState = Object.assign({}, state)
      if (action.field === 'role') {
        newState.scope = ROLES[action.value]
      }
      newState[action.field] = action.value
      return newState
    }
    case RESET_USER_PAGE:
      return generateBlankUser()
    default: {
      return state
    }
  }
}

function changePassword(state = generateBlankPWState(), action) {
  switch (action.type) {
    case RESET_PW_STATE: {
      return generateBlankPWState()
    }
    case UPDATE_NEW_PASSWORD: {
      return Object.assign({}, state, { [action.field]: action.value })
    }
    case SAVED_USER:
    case RESET_USER_PAGE:
      return generateBlankPWState()
    default: {
      return state
    }
  }
}

function newPasswordActive(state = false, action) {
  switch (action.type) {
    case SHOW_CHANGE_PASSWORD:
      return true
    case SAVED_USER:
    case RESET_USER_PAGE:
      return false
    default:
      return state
  }
}

function profilePic(state = null, action) {
  switch (action.type) {
    case UPLOADED_IMAGE_PREVIEW:
      return action.image
    case RESET_USER_PAGE:
    case CLEAR_USER_PIC:
      return null
    default:
      return state
  }
}

export default combineReducers({
  user,
  changePassword,
  newPasswordActive,
  profilePic
})

export const actions = {
  updateField,
  updateNewPassword,
  showChangePassword,
  saveToDatabase,
  fetchUser,
  saveSelfToDatabase,
  uploadProfilePic,
  deleteUser,
  resetUserPage
}
